import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Input } from 'reactstrap';


function ReadOnlyAnnotationCanvas({
    image,
    annotations = [],
  }) {
    const canvasRef = useRef(null);
    const imgRef = useRef(new window.Image());
    const [imgLoaded, setImgLoaded] = useState(false);
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
    const [hoveredAnnotation, setHoveredAnnotation] = useState(null);
  
    // --- IMAGE LOADING ---
    useEffect(() => {
      setImgLoaded(false);
      const imageElement = imgRef.current;
      
      const handleLoad = () => {
        const maxWidth = 800;
        const maxHeight = 600;
        const aspect = imageElement.width / imageElement.height;
        let w, h;
  
        if (aspect > maxWidth / maxHeight) {
          w = maxWidth;
          h = maxWidth / aspect;
        } else {
          h = maxHeight;
          w = maxHeight * aspect;
        }
  
        setCanvasSize({ width: w, height: h });
  
        if (canvasRef.current) {
          const ctx = canvasRef.current.getContext('2d');
          canvasRef.current.width = w;
          canvasRef.current.height = h;
          ctx.drawImage(imageElement, 0, 0, w, h);
          setImgLoaded(true);
        }
      };
  
      imageElement.onload = handleLoad;
      // Check if image object exists before assigning src
      if (image && image.filePath) {
          imageElement.src = image.filePath;
      }
  
      return () => {
          imageElement.onload = null;
      };
    }, [image]);
  
    // --- RENDERING HELPERS ---
    const drawAnnotationShapePath = useCallback((ctx, annotation) => {
      const coords = annotation.coordinates;
      switch (annotation.type) {
        case 'circle': {
          const centerX = coords[0] * canvasSize.width;
          const centerY = coords[1] * canvasSize.height;
          const radius = coords[2] * Math.sqrt(canvasSize.width * canvasSize.height);
          ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
          break;
        }
        case 'rectangle': {
          const x = coords[0] * canvasSize.width;
          const y = coords[1] * canvasSize.height;
          const width = coords[2] * canvasSize.width;
          const height = coords[3] * canvasSize.height;
          ctx.rect(x, y, width, height);
          break;
        }
        case 'freeform': {
          if (coords.length >= 4) {
            ctx.moveTo(coords[0] * canvasSize.width, coords[1] * canvasSize.height);
            for (let i = 2; i < coords.length; i += 2) {
              ctx.lineTo(coords[i] * canvasSize.width, coords[i + 1] * canvasSize.height);
            }
          }
          break;
        }
        default:
          break;
      }
    }, [canvasSize]);
  
    const drawAnnotation = useCallback((ctx, annotation, isHighlighted = false) => {
      ctx.strokeStyle = annotation.color;
      ctx.lineWidth = isHighlighted ? 4 : 2;
      ctx.shadowColor = isHighlighted ? annotation.color : 'transparent';
      ctx.shadowBlur = isHighlighted ? 10 : 0;
      ctx.fillStyle = "transparent";
  
      ctx.beginPath();
      drawAnnotationShapePath(ctx, annotation);
      ctx.stroke();
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    }, [drawAnnotationShapePath]);
  
    const redrawCanvas = useCallback(() => {
      if (!canvasRef.current || !imgLoaded || !imgRef.current) return;
      const ctx = canvasRef.current.getContext('2d');
      
      // Clear and redraw background image
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(imgRef.current, 0, 0, canvasSize.width, canvasSize.height);
  
      if (hoveredAnnotation) {
        // Dim background effect for hover
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);
  
        // Cut out the shape of the hovered annotation
        ctx.save();
        ctx.beginPath();
        drawAnnotationShapePath(ctx, hoveredAnnotation);
        ctx.clip();
        ctx.drawImage(imgRef.current, 0, 0, canvasSize.width, canvasSize.height);
        ctx.restore();
  
        // Draw the highlighted border
        drawAnnotation(ctx, hoveredAnnotation, true);
      } else {
        // Draw all annotations normally
        annotations && annotations.forEach(annotation => drawAnnotation(ctx, annotation));
      }
    }, [annotations, imgLoaded, hoveredAnnotation, canvasSize, drawAnnotationShapePath, drawAnnotation]);
  
    // Trigger redraw when dependencies change
    useEffect(() => {
      if (imgLoaded) {
        redrawCanvas();
      }
    }, [annotations, imgLoaded, hoveredAnnotation, redrawCanvas]);
  
    // --- UTILS ---
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    };
  
    return (
      <Row className="p-4">
        <Col md={8}>
          <canvas
            ref={canvasRef}
            className="annotation-canvas"
            style={{
              cursor: 'default',
              border: '2px solid #e5e7eb',
              borderRadius: '8px'
            }}
          />
        </Col>
  
        <Col md={4}>
          <div className='comments-section' style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <h6 className="mb-3">Annotations</h6>
            
            {annotations && [...annotations]
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .map((annotation, index) => {
                return (
                  <div
                    key={annotation.id || index}
                    className='annotation-comments'
                    style={{
                      padding: '10px',
                      marginBottom: '10px',
                      border: '1px solid #eee',
                      borderRadius: '5px'
                    }}
                    onMouseEnter={() => setHoveredAnnotation(annotation)}
                    onMouseLeave={() => setHoveredAnnotation(null)}
                  >
                    <div className='annotation-info mb-1'>
                      <span className="annotation-number mr-2" style={{ fontWeight: 'bold' }}>{index + 1}.</span>
                      <span className="text-muted">{annotation.userName || "Anonymous"}</span>
                    </div>
                    
                    <div className="text-muted small mb-2">
                      {formatDate(annotation.createdAt)}
                    </div>
                    
                    <div>
                      <b>{annotation.comment}</b>
                    </div>
                  </div>
                );
              })}
              
              {(!annotations || annotations.length === 0) && (
                  <p className="text-muted text-center mt-4">No annotations available.</p>
              )}
          </div>
        </Col>
      </Row>
    );
  }

function PhotoDisplay({ image, annotations = [] }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageElement, setImageElement] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [hoveredAnnotation, setHoveredAnnotation] = useState(null);

  // Load and setup image
  useEffect(() => {
    if (!image || !image.filePath) return;
    const img = new Image();
    img.onload = () => {
      console.log("Image loaded successfully");
      setImageElement(img);
      setImageLoaded(true);
      setImageError(false);
    };
    img.onerror = (err) => {
      console.error("Image failed to load:", image.filePath, err);
      setImageError(true);
      setImageLoaded(false);
    };
    img.src = image.filePath;
  }, [image && image.filePath]);


  // Setup canvas dimensions
  useEffect(() => {
    if (!imageLoaded || !imageElement || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const maxWidth = 800;
    const maxHeight = 600;
    const aspectRatio = imageElement.naturalWidth / imageElement.naturalHeight;
    let width, height;
    if (aspectRatio > maxWidth / maxHeight) {
      width = Math.min(maxWidth, imageElement.naturalWidth);
      height = width / aspectRatio;
    } else {
      height = Math.min(maxHeight, imageElement.naturalHeight);
      width = height * aspectRatio;
    }
    canvas.width = width;
    canvas.height = height;
    setCanvasSize({ width, height });
  }, [imageLoaded, imageElement, canvasRef.current]);

  // Draw canvas content
  const drawCanvas = useCallback(() => {
    if (!imageElement || !canvasRef.current || canvasSize.width === 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image initially
    ctx.drawImage(imageElement, 0, 0, canvasSize.width, canvasSize.height);

    if (hoveredAnnotation) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);

      ctx.save();
      ctx.beginPath();
      drawAnnotationShapePath(ctx, hoveredAnnotation);
      ctx.clip();

      ctx.drawImage(imageElement, 0, 0, canvasSize.width, canvasSize.height);
      ctx.restore(); 

      ctx.save();
      ctx.strokeStyle = hoveredAnnotation.color || '#3b82f6';
      ctx.lineWidth = 4; 
      ctx.shadowColor = hoveredAnnotation.color || '#3b82f6';
      ctx.shadowBlur = 10;
      drawAnnotationShape(ctx, hoveredAnnotation);
      ctx.restore();
    } else {
      annotations && annotations.forEach(annotation => {
        ctx.save();
        ctx.strokeStyle = annotation.color || '#3b82f6';
        ctx.lineWidth = 2;
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        drawAnnotationShape(ctx, annotation);
        ctx.restore();
      });
    }

  }, [imageElement, canvasSize, annotations, hoveredAnnotation]);

  // Helper to draw the path for clipping (without stroke/fill)
  const drawAnnotationShapePath = (ctx, annotation) => {
    const coords = annotation.coordinates;
    switch (annotation.type) {
      case 'circle': {
        const centerX = coords[0] * canvasSize.width;
        const centerY = coords[1] * canvasSize.height;
        const radius = coords[2] * Math.min(canvasSize.width, canvasSize.height);
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        break;
      }
      case 'rectangle': {
        const x = coords[0] * canvasSize.width;
        const y = coords[1] * canvasSize.height;
        const width = coords[2] * canvasSize.width;
        const height = coords[3] * canvasSize.height;
        ctx.rect(x, y, width, height);
        break;
      }
      case 'freeform': {
        if (coords.length >= 4) {
          ctx.moveTo(coords[0] * canvasSize.width, coords[1] * canvasSize.height);
          for (let i = 2; i < coords.length; i += 2) {
            ctx.lineTo(coords[i] * canvasSize.width, coords[i + 1] * canvasSize.height);
          }
          ctx.closePath();
        }
        break;
      }
      default:
        break;
    }
  };


  
  const drawAnnotationShape = (ctx, annotation) => {
    const coords = annotation.coordinates;
    ctx.beginPath(); 
    switch (annotation.type) {
      case 'circle': {
        const centerX = coords[0] * canvasSize.width;
        const centerY = coords[1] * canvasSize.height;
        const radius = coords[2] * Math.min(canvasSize.width, canvasSize.height);
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        break;
      }
      case 'rectangle': {
        const x = coords[0] * canvasSize.width;
        const y = coords[1] * canvasSize.height;
        const width = coords[2] * canvasSize.width;
        const height = coords[3] * canvasSize.height;
        ctx.rect(x, y, width, height);
        break;
      }
      case 'freeform': {
        if (coords.length >= 4) {
          ctx.moveTo(coords[0] * canvasSize.width, coords[1] * canvasSize.height);
          for (let i = 2; i < coords.length; i += 2) {
            ctx.lineTo(coords[i] * canvasSize.width, coords[i + 1] * canvasSize.height);
          }
        }
        break;
      }
      default:
        break;
    }
    ctx.stroke();
  };

  // Redraw canvas when dependencies change
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Get mouse position relative to canvas
  const getMousePosition = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasSize.width / rect.width;
    const scaleY = canvasSize.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }, [canvasSize]);

  // Modified to handle multiple circles
  const isPointInAnnotation = useCallback((point, annotation) => {
    const tolerance = 10;
    const coords = annotation.coordinates;
    const ctx = canvasRef.current.getContext('2d');

    // For precise hit detection, draw the path and use isPointInStroke
    ctx.save();
    ctx.beginPath();
    drawAnnotationShapePath(ctx, annotation);
    ctx.lineWidth = tolerance * 2; 
    const hit = ctx.isPointInStroke(point.x, point.y);
    ctx.restore();
    return hit;

  }, [canvasSize]);

  // Handle mouse movement
  const handleMouseMove = useCallback((e) => {
    if (canvasSize.width === 0 || !canvasRef.current) return;
    const mousePos = getMousePosition(e);
    const foundAnnotation = annotations && annotations.find(annotation => isPointInAnnotation(mousePos, annotation));
    if (foundAnnotation) {
      setHoveredAnnotation(foundAnnotation);
      canvasRef.current.style.cursor = 'pointer';
    } else {
      setHoveredAnnotation(null);
      canvasRef.current.style.cursor = 'default';
    }
  }, [annotations, getMousePosition, isPointInAnnotation, canvasSize.width]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    setHoveredAnnotation(null);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'default';
    }
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    // Create a new Date object from the input string
    const date = new Date(dateString);
  
    // Get individual date and time components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed, so we add 1
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
  
    // Combine them into the desired format
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Loading state
  if (!imageLoaded && !imageError) {
    return (
      <div className="photo-display-loading">
        <div className="loading-spinner"></div>
        <p>Loading image...</p>
      </div>
    );
  }

  if (imageError) {
    return (
      <div className="photo-display-error">
        <div className="error-icon">⚠️</div>
        <p>Error loading image</p>
        <button className="retry-button" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="photo-container">
      {hoveredAnnotation && hoveredAnnotation.comment && (
        <div
          className="photo-annotation-tooltip"
          style={{ borderColor: hoveredAnnotation.color || "#3b82f6" }}
        >
          <div className="tooltip-header">{hoveredAnnotation.comment}</div>
          <div className="tooltip-footer">
            <span>{hoveredAnnotation.userName || "Anonymous"}</span>
            <span>{formatDate(hoveredAnnotation.createdAt)}</span>
          </div>
        </div>
      )}

      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="photo-canvas"
      />
    </div>
  );
}
export { PhotoDisplay };
export default ReadOnlyAnnotationCanvas